import React, { useState, useEffect, useContext, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
    Shield, 
    MonitorSmartphone, 
    Globe, 
    Clock, 
    LogOut, 
    CheckCircle2, 
    AlertTriangle, 
    AlertCircle,
    Laptop,
    Smartphone,
    Monitor,
    Loader2,
    Info,
    HelpCircle,
    UserCheck,
    SearchCode,
    CalendarDays
} from 'lucide-react';
import { SnackbarContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../api/api';
import { useConfirm } from '../contexts/ConfirmContext';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Skeleton } from './ui/skeleton';
import EmptyState from './ui/EmptyState';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Separator } from './ui/separator';
import { 
    Tooltip, 
    TooltipContent, 
    TooltipProvider, 
    TooltipTrigger 
} from './ui/tooltip';

export default function Sessions() {
    const showSnackbar = useContext(SnackbarContext);

    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [revokingId, setRevokingId] = useState(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const selectedSessionId = searchParams.get('sessionId');

    const sessionRefs = useRef({});

    const fetchSessions = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/sessions/my');
            setSessions(res.data);
            
            // If the selected session is no longer in the list, clear it
            if (selectedSessionId && !res.data.find(s => s.id === selectedSessionId)) {
                setSearchParams({}, { replace: true });
            }
        } catch (error) {
            handleApiError(error, 'Error fetching active sessions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSessions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-scroll to selected session on deep-link load
    useEffect(() => {
        if (!loading && selectedSessionId && sessionRefs.current[selectedSessionId]) {
            setTimeout(() => {
                sessionRefs.current[selectedSessionId].scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
            }, 300); // Small delay to allow for page-shell animations
        }
    }, [loading, selectedSessionId]);

    const handleApiError = (error, defaultMsg) => {
        if (error.isAppError) {
            showSnackbar(`${error.code}: ${error.message}`, 'error');
        } else {
            showSnackbar(defaultMsg, 'error');
        }
    };

    const confirm = useConfirm();

    const handleRevoke = async (id, isCurrent) => {
        let title, message;
        if (isCurrent) {
            title = "Log Out?";
            message = "Are you sure you want to log out of this current device? You will need to sign in again to access your account.";
        } else {
            title = "Revoke Device Access?";
            message = "Danger: This device will be disconnected on its next attempt to use Mailwise. This action is irreversible and recorded in the audit log.";
        }

        const ok = await confirm(title, message);
        if (!ok) return;

        setRevokingId(id);
        try {
            await api.post(`/api/sessions/${id}/revoke`, {});

            if (isCurrent) {
                showSnackbar('Signed out of this device successfully.', 'success');
                localStorage.removeItem('token');
                window.location.href = '/login';
            } else {
                showSnackbar('Session access revoked. The device will be disconnected on its next request.', 'success');
                // Clear param if we revoke the selected one
                if (id === selectedSessionId) {
                    setSearchParams({});
                }
                fetchSessions();
            }
        } catch (error) {
            if (error.response?.status === 404) {
                showSnackbar('This session may have already been revoked or expired.', 'error');
                fetchSessions();
            } else {
                handleApiError(error, 'Failed to revoke session access');
            }
        } finally {
            setRevokingId(null);
        }
    };

    const handleSelectSession = (id) => {
        if (selectedSessionId === id) {
            setSearchParams({});
        } else {
            setSearchParams({ sessionId: id });
        }
    };

    const parseUserAgent = (ua) => {
        if (!ua || ua === 'unknown') return { 
            name: 'Unknown Device', 
            icon: MonitorSmartphone, 
            isHeuristic: true,
            description: 'The device type could not be determined from the provided metadata.'
        };
        
        let os = 'Unknown OS';
        let Icon = MonitorSmartphone;
        let osDescription = 'an unidentified operating system';

        if (ua.includes('Win')) { os = 'Windows PC'; Icon = Monitor; osDescription = 'a Windows-based desktop or laptop'; }
        else if (ua.includes('Mac')) { os = 'Mac / OSX'; Icon = Laptop; osDescription = 'an Apple Macintosh computer'; }
        else if (ua.includes('Linux')) { os = 'Linux PC'; Icon = Monitor; osDescription = 'a Linux-based workstation'; }
        else if (ua.includes('Android')) { os = 'Android Device'; Icon = Smartphone; osDescription = 'a mobile device running Android'; }
        else if (ua.includes('iPhone') || ua.includes('iPad')) { os = 'iOS Device'; Icon = Smartphone; osDescription = 'an Apple iPhone or iPad'; }

        let browser = 'Unknown Browser';
        let browserDescription = 'an unidentified web browser';
        if (ua.includes('Edg/')) { browser = 'Edge'; browserDescription = 'the Microsoft Edge browser'; }
        else if (ua.includes('Chrome')) { browser = 'Chrome'; browserDescription = 'the Google Chrome browser'; }
        else if (ua.includes('Firefox')) { browser = 'Firefox'; browserDescription = 'the Mozilla Firefox browser'; }
        else if (ua.includes('Safari') && !ua.includes('Chrome')) { browser = 'Safari'; browserDescription = 'the Apple Safari browser'; }
        else if (ua.includes('Opera') || ua.includes('OPR/')) { browser = 'Opera'; browserDescription = 'the Opera browser'; }

        return { 
            name: `${browser} on ${os}`, 
            icon: Icon,
            isHeuristic: true,
            raw: ua,
            description: `This session was established using ${browserDescription} on ${osDescription}.`
        };
    };

    const SessionSkeleton = () => (
        <div className="space-y-12 animate-in fade-in duration-500">
            <div>
                <Skeleton className="h-6 w-32 mb-4" />
                <Card className="border-primary/10 shadow-lg shadow-primary/5">
                    <CardContent className="p-8">
                        <div className="flex gap-6">
                            <Skeleton className="h-20 w-20 rounded-2xl shrink-0" />
                            <div className="space-y-4 flex-1">
                                <div className="flex items-center gap-3">
                                    <Skeleton className="h-7 w-64" />
                                    <Skeleton className="h-5 w-24" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <Skeleton className="h-5 w-full" />
                                    <Skeleton className="h-5 w-full" />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
            <div className="space-y-6">
                <Skeleton className="h-6 w-48" />
                <div className="grid gap-4">
                    <Skeleton className="h-24 w-full rounded-2xl" />
                    <Skeleton className="h-24 w-full rounded-2xl" />
                </div>
            </div>
        </div>
    );

    if (loading) return <PageShell><SessionSkeleton /></PageShell>;

    const currentSession = sessions.find(s => s.isCurrent);
    const otherSessions = sessions.filter(s => !s.isCurrent);
    const suspiciousCount = sessions.filter(s => s.ipAddress === 'unknown').length;

    return (
        <PageShell>
            <PageHeader
                title="Security Workstation: Active Sessions"
                subtitle="Review and manage every device currently authenticated to your Mailwise account. Act immediately if you detect unrecognized activity."
                icon={Shield}
            />

            {suspiciousCount > 0 && (
                <Alert variant="destructive" className="bg-amber-50 border-amber-200 text-amber-900 mb-8">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <AlertTitle className="font-bold">Security Review Recommended</AlertTitle>
                    <AlertDescription>
                        We detected sessions with unidentified IP addresses. Please review the "Other Active Sessions" list carefully.
                    </AlertDescription>
                </Alert>
            )}

            <div className="space-y-12">
                {/* Current Session Section */}
                {currentSession && (
                    <section>
                        <div className="flex items-center gap-2 mb-4">
                            <h2 className="text-xl font-bold tracking-tight">This Device</h2>
                            <Separator className="flex-1" />
                        </div>
                        <Card 
                            ref={el => sessionRefs.current[currentSession.id] = el}
                            className={`transition-all duration-500 overflow-hidden ring-offset-background ${selectedSessionId === currentSession.id ? 'border-primary shadow-2xl ring-2 ring-primary/20 bg-primary/10' : 'border-primary/20 bg-primary/5 shadow-xl shadow-primary/5 ring-1 ring-primary/20'}`}
                            onClick={() => handleSelectSession(currentSession.id)}
                        >
                            <CardContent className="p-0">
                                <div className="flex flex-col md:flex-row md:items-center cursor-pointer">
                                    <div className="p-8 flex items-center gap-6 flex-1">
                                        <div className={`p-4 rounded-2xl bg-primary text-white shadow-lg shadow-primary/20 transition-transform ${selectedSessionId === currentSession.id ? 'scale-110' : ''}`}>
                                            {React.createElement(parseUserAgent(currentSession.userAgent).icon, { className: "h-8 w-8" })}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-3 mb-1">
                                                <h3 className="text-xl font-bold font-heading tracking-tight">{parseUserAgent(currentSession.userAgent).name}</h3>
                                                <Badge className="bg-green-600 font-bold tracking-wider text-[10px] uppercase px-2 py-0.5">
                                                    Current Session
                                                </Badge>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-6 text-sm font-medium text-muted-foreground">
                                                <div className="flex items-center gap-2 group/ip">
                                                    <Globe className="h-3.5 w-3.5 opacity-60" />
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <span className="cursor-help">IP: <span className="text-foreground">{currentSession.ipAddress}</span></span>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top" className="max-w-[200px]">
                                                                The last recorded network location of this device.
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                    <Info className="h-3 w-3 opacity-0 group-hover/ip:opacity-40 transition-opacity" />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-3.5 w-3.5 opacity-60" />
                                                    <span>Last activity recorded: <span className="text-foreground font-bold text-primary italic">Live</span></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-primary/10 p-6 flex items-center justify-center border-t md:border-t-0 md:border-l border-primary/10" onClick={(e) => e.stopPropagation()}>
                                        <Button
                                            variant="outline"
                                            onClick={() => handleRevoke(currentSession.id, true)}
                                            className="bg-background hover:bg-muted font-bold group shadow-sm"
                                            disabled={revokingId === currentSession.id}
                                        >
                                            {revokingId === currentSession.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogOut className="mr-2 h-4 w-4 group-hover:text-primary transition-colors" />}
                                            Log Out This Device
                                        </Button>
                                    </div>
                                </div>
                                {selectedSessionId === currentSession.id && (
                                    <div className="p-8 pt-0 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <Separator className="mb-6 bg-primary/10" />
                                        
                                        <div className="grid md:grid-cols-2 gap-8">
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-2 text-primary">
                                                    <UserCheck className="h-4 w-4" />
                                                    <h4 className="text-sm font-bold uppercase tracking-tight">Identity Explanation</h4>
                                                </div>
                                                <p className="text-sm text-muted-foreground leading-relaxed">
                                                    {parseUserAgent(currentSession.userAgent).description} This is the specific device and browser combination that you are using right now to access this page.
                                                </p>
                                                <div className="bg-background/50 rounded-xl p-4 border border-primary/10 space-y-2">
                                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-primary/70 flex items-center gap-1.5">
                                                        <SearchCode className="h-3 w-3" />
                                                        Technical JTI Identification
                                                    </h4>
                                                    <p className="text-[10px] font-mono text-muted-foreground break-all leading-relaxed opacity-60">
                                                        {currentSession.jti || 'LEGACY_STATELESS_TOKEN'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                <div className="flex items-center gap-2 text-primary">
                                                    <HelpCircle className="h-4 w-4" />
                                                    <h4 className="text-sm font-bold uppercase tracking-tight">Current Session Info</h4>
                                                </div>
                                                <ul className="text-xs text-muted-foreground space-y-2">
                                                    <li className="flex gap-2">
                                                        <div className="h-1 w-1 rounded-full bg-primary mt-1.5 shrink-0" />
                                                        <span><strong>Active Authorization:</strong> This device has an active cryptographic token verifying your identity.</span>
                                                    </li>
                                                    <li className="flex gap-2">
                                                        <div className="h-1 w-1 rounded-full bg-primary mt-1.5 shrink-0" />
                                                        <span><strong>Session Authority:</strong> It is designated as "Current" because it initiated the web request you are currently viewing.</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                        <p className="mt-4 text-xs text-muted-foreground font-medium flex items-center gap-2">
                            <AlertCircle className="h-3.5 w-3.5" />
                            Revoking this session will log you out of your current Mailwise workspace immediately.
                        </p>
                    </section>
                )}

                {/* Other Sessions Section */}
                <section>
                    <div className="flex items-center gap-2 mb-6">
                        <h2 className="text-xl font-bold tracking-tight">Other Active Sessions</h2>
                        <Badge variant="outline" className="font-bold opacity-60 px-2 py-0">
                            {otherSessions.length}
                        </Badge>
                        <Separator className="flex-1" />
                    </div>

                    {otherSessions.length > 0 ? (
                        <div className="grid gap-4">
                            {otherSessions.map(session => (
                                <Card 
                                    key={session.id} 
                                    ref={el => sessionRefs.current[session.id] = el}
                                    className={`group transition-all duration-300 hover:shadow-lg cursor-pointer ${selectedSessionId === session.id ? 'border-primary ring-2 ring-primary/10 bg-primary/5 shadow-md scale-[1.01]' : 'bg-card/50 hover:border-primary/30'}`}
                                    onClick={() => handleSelectSession(session.id)}
                                >
                                    <div className="p-6 flex flex-col md:flex-row md:items-center gap-6">
                                        <div className="flex items-center gap-5 flex-1">
                                            <div className={`p-3 rounded-xl transition-colors ${selectedSessionId === session.id ? 'bg-primary text-white shadow-md' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'}`}>
                                                {React.createElement(parseUserAgent(session.userAgent).icon, { className: "h-6 w-6" })}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h3 className="font-bold text-base">{parseUserAgent(session.userAgent).name}</h3>
                                                    {parseUserAgent(session.userAgent).isHeuristic && (
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Badge variant="outline" className="text-[9px] uppercase font-black tracking-widest opacity-40 cursor-help">
                                                                        Estimated ID
                                                                    </Badge>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="top">
                                                                    This identification is estimated from the software version headers provided by the device.
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-1 gap-x-8 text-xs font-semibold text-muted-foreground">
                                                    <div className="flex items-center gap-1.5 group/ip">
                                                        <Globe className="h-3 w-3 opacity-50" />
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <span className="cursor-help">IP: <span className="text-foreground">{session.ipAddress}</span></span>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="top">
                                                                    The network address from which this device last connected.
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                        <Info className="h-2.5 w-2.5 opacity-0 group-hover/ip:opacity-30 transition-opacity" />
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <Clock className="h-3 w-3 opacity-50" />
                                                        <span>Last Activity Recorded: <span className="text-foreground">{new Date(session.lastSeenAt).toLocaleString()}</span></span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <CalendarDays className="h-3 w-3 opacity-50" />
                                                        <span>Initial Session Start: <span className="text-foreground">{new Date(session.issuedAt).toLocaleDateString()}</span></span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="shrink-0 flex justify-end" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                className={`font-bold transition-all ${selectedSessionId === session.id ? 'bg-red-600 text-white shadow-lg' : 'bg-red-50 hover:bg-red-500 hover:text-white text-red-600 border-red-100 shadow-none'}`}
                                                onClick={() => handleRevoke(session.id, false)}
                                                disabled={revokingId === session.id}
                                            >
                                                {revokingId === session.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Revoke Access'}
                                            </Button>
                                        </div>
                                    </div>
                                    {selectedSessionId === session.id && (
                                        <div className="p-6 pt-0 animate-in fade-in slide-in-from-top-2 duration-300">
                                            <Separator className="mb-6" />
                                            
                                            <div className="grid md:grid-cols-2 gap-8">
                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-2 text-foreground">
                                                        <UserCheck className="h-4 w-4 opacity-70" />
                                                        <h4 className="text-sm font-bold uppercase tracking-tight">Identity Explanation</h4>
                                                    </div>
                                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                                        {parseUserAgent(session.userAgent).description} This information is derived from the device metadata provided by the browser.
                                                    </p>
                                                    <div className="bg-muted/50 rounded-xl p-4 border border-border space-y-2">
                                                        <h4 className="text-[10px] font-black uppercase tracking-widest opacity-50 flex items-center gap-1.5">
                                                            <SearchCode className="h-3 w-3" />
                                                            Technical JTI Identification
                                                        </h4>
                                                        <p className="text-[10px] font-mono text-muted-foreground break-all leading-relaxed opacity-60">
                                                            {session.jti || 'LEGACY_STATELESS_TOKEN'}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-2 text-foreground">
                                                        <HelpCircle className="h-4 w-4 opacity-70" />
                                                        <h4 className="text-sm font-bold uppercase tracking-tight">Security Review Checklist</h4>
                                                    </div>
                                                    <ul className="text-xs text-muted-foreground space-y-3">
                                                        <li className="flex gap-2">
                                                            <div className="h-1 w-1 rounded-full bg-primary mt-1.5 shrink-0" />
                                                            <div>
                                                                <p className="font-bold text-foreground">Do you recognize this device?</p>
                                                                <p className="opacity-70">Compare the OS and browser names above with the devices you own.</p>
                                                            </div>
                                                        </li>
                                                        <li className="flex gap-2">
                                                            <div className="h-1 w-1 rounded-full bg-primary mt-1.5 shrink-0" />
                                                            <div>
                                                                <p className="font-bold text-foreground">Does the location align with your history?</p>
                                                                <p className="opacity-70">Check if the IP address belongs to your home, office, or known VPN network.</p>
                                                            </div>
                                                        </li>
                                                        <li className="flex gap-2">
                                                            <div className="h-1 w-1 rounded-full bg-primary mt-1.5 shrink-0" />
                                                            <div>
                                                                <p className="font-bold text-foreground">Does the timing make sense?</p>
                                                                <p className="opacity-70">Was this session established at a time when you were signed in to Mailwise?</p>
                                                            </div>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <EmptyState
                            title="No other active sessions found"
                            description="This device is the only active session currently tracked."
                            icon={CheckCircle2}
                        />
                    )}
                </section>

                {/* Footer Security Disclaimer */}
                <Card className="bg-muted/10 border-dashed border-2 shadow-none">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Shield className="h-5 w-5 text-primary" />
                            Security & Revocation Truthfulness
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm font-bold">
                                    <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                    Truthful Revocation
                                </div>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                    Revoking a session immediately invalidates the underlying cryptographic token (JTI) in our central auth registry. Any subsequent requests from that device will be rejected with a <code>401 Unauthorized</code> status.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm font-bold">
                                    <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                    Estimated Identification
                                </div>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                    Device metadata is derived from the non-unique <code>User-Agent</code> header. This is a <strong>heuristic estimate</strong> and should be used as a security guide, not as a definitive hardware signature.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </PageShell>
    );
}
